import { useState } from "react";
import { useOrgSettings } from "./OrgSettingsContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ExternalLink,
  User,
  Key,
  Code,
  Loader2,
  Trash2,
  AlertCircle,
  Copy,
  ChevronDown,
  ChevronRight,
  Info,
  Palette,
} from "lucide-react";
import { apiConfig, buildApiUrl } from "../../lib/config";
import { toast } from "../../hooks/use-toast";

export function PortalSettings() {
  const {
    organizationSubdomain,
    formState,
    updateFormState,
    portalKeyStatus,
    generatedPortalKey,
    isLoadingKeyStatus,
    isGeneratingKey,
    isRevokingKey,
    generatePortalKey,
    revokePortalKey,
    copyPortalKey,
  } = useOrgSettings();

  const [logoError, setLogoError] = useState(false);

  const [isIntegrationDocsExpanded, setIsIntegrationDocsExpanded] =
    useState(false);

  const handleCopyCode = () => {
    const backendUrl =
      import.meta.env.VITE_BACKEND_URL || apiConfig.baseUrl.replace("/api", "");
    const code = `// Redirect user to customer portal
const baseUrl = '${backendUrl}';
const orgSlug = '${organizationSubdomain || "your-org-slug"}';
const email = 'customer@example.com'; // Get from your user context
const portalKey = 'YOUR_PUBLIC_PORTAL_KEY';

const loginUrl = \`\${baseUrl}/api/org/\${orgSlug}/portal/public-auto-login?email=\${encodeURIComponent(email)}&key=\${portalKey}\`;

// Redirect user to auto-login
window.location.href = loginUrl;`;
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied!",
      description: "Code example copied to clipboard",
    });
  };

  // Reset logo error when URL changes
  const handleLogoUrlChange = (url: string) => {
    setLogoError(false);
    updateFormState({ logoUrl: url });
  };

  return (
    <div className="space-y-6">
      {/* Branding Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Branding
          </CardTitle>
          <CardDescription>
            Customize your organization's visual identity in the customer portal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo URL */}
          <div className="space-y-2">
            <Label htmlFor="logoUrl">Logo URL</Label>
            <Input
              id="logoUrl"
              value={formState.logoUrl}
              placeholder="https://example.com/logo.png"
              onChange={(e) => handleLogoUrlChange(e.target.value)}
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter a URL to your organization's logo image.
            </p>
          </div>

          {/* Logo Preview */}
          {formState.logoUrl && !logoError && (
            <div className="flex flex-col gap-2">
              <Label>Preview</Label>
              <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 w-fit">
                <img
                  src={formState.logoUrl}
                  alt="Logo preview"
                  className="h-12 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              </div>
            </div>
          )}

          {/* Colors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={formState.primaryColor}
                  className="w-16 p-1 h-10 cursor-pointer"
                  onChange={(e) =>
                    updateFormState({ primaryColor: e.target.value })
                  }
                />
                <Input
                  value={formState.primaryColor}
                  placeholder="#3B82F6"
                  onChange={(e) =>
                    updateFormState({ primaryColor: e.target.value })
                  }
                  className="flex-1"
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Used for buttons and primary actions.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondaryColor">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={formState.secondaryColor}
                  className="w-16 p-1 h-10 cursor-pointer"
                  onChange={(e) =>
                    updateFormState({ secondaryColor: e.target.value })
                  }
                />
                <Input
                  value={formState.secondaryColor}
                  placeholder="#6B7280"
                  onChange={(e) =>
                    updateFormState({ secondaryColor: e.target.value })
                  }
                  className="flex-1"
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Used for secondary elements and accents.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Portal Link */}
      {organizationSubdomain && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Customer Portal
            </CardTitle>
            <CardDescription>
              Direct link to your customer-facing support portal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
                <a
                  href={`${window.location.origin}/org/${organizationSubdomain}/portal`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Customer Portal
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Access Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Access Settings
          </CardTitle>
          <CardDescription>
            Control how customers can access your support portal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Self Registration */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 flex-1">
              <Label
                htmlFor="allowCustomerSelfRegistration"
                className="font-medium"
              >
                Allow Customer Self-Registration
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Allow new customers to register through the portal.
              </p>
            </div>
            <Switch
              id="allowCustomerSelfRegistration"
              checked={formState.allowCustomerSelfRegistration}
              onCheckedChange={(checked) =>
                updateFormState({ allowCustomerSelfRegistration: checked })
              }
            />
          </div>

          {/* Public Knowledge Base */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-1 flex-1">
              <Label htmlFor="isKnowledgeBasePublic" className="font-medium">
                Public Knowledge Base
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Make knowledge base articles publicly accessible without login.
              </p>
            </div>
            <Switch
              id="isKnowledgeBasePublic"
              checked={formState.isKnowledgeBasePublic}
              onCheckedChange={(checked) =>
                updateFormState({ isKnowledgeBasePublic: checked })
              }
            />
          </div>

          {/* Sitemap: how search engines find the public knowledge base's articles */}
          {formState.isKnowledgeBasePublic && organizationSubdomain && (() => {
            const sitemapUrl = new URL(
              buildApiUrl(`customer-portal/knowledge-base/sitemap/${encodeURIComponent(organizationSubdomain)}`),
              window.location.origin
            ).toString();
            return (
              <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Label htmlFor="knowledgeBaseSitemap" className="font-medium">
                  Knowledge Base Sitemap
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Lists your public articles for search engines. Submit it in Google Search Console or Bing Webmaster
                  Tools so customers can find your answers by searching the web.
                </p>
                <div className="flex gap-2">
                  <Input id="knowledgeBaseSitemap" readOnly value={sitemapUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Copy the sitemap address"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(sitemapUrl);
                        toast({ title: "Copied", description: "Sitemap address copied to the clipboard." });
                      } catch {
                        toast({ title: "Copy failed", description: "Select the address and copy it instead.", variant: "destructive" });
                      }
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })()}

          {/* Article review interval */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-1 flex-1">
              <Label htmlFor="articleReviewIntervalDays" className="font-medium">
                Review Articles Every
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Published articles not reviewed (or published) within this many days are listed under Needs
                Attention in the knowledge base, so outdated answers get checked. 0 turns it off.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="articleReviewIntervalDays"
                type="number"
                min={0}
                max={3650}
                value={formState.articleReviewIntervalDays}
                className="w-24"
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 0 && value <= 3650) {
                    updateFormState({ articleReviewIntervalDays: value });
                  }
                }}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">days</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* External Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            External Integration
          </CardTitle>
          <CardDescription>
            Generate a public portal key for seamless customer portal
            authentication from external applications.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingKeyStatus ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Existing Key Display */}
              {portalKeyStatus?.hasPortalKey && !generatedPortalKey && (
                <div className="space-y-2">
                  <Label>Current Public Portal Key</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={portalKeyStatus.maskedPortalKey || ""}
                      disabled
                      className="bg-gray-50 dark:bg-gray-800 font-mono text-sm"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={revokePortalKey}
                      disabled={isRevokingKey}
                    >
                      {isRevokingKey ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {portalKeyStatus.createdAt && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Created:{" "}
                      {new Date(portalKeyStatus.createdAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {/* Newly Generated Key Warning */}
              {generatedPortalKey && (
                <div className="space-y-2">
                  <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          Save your public portal key now!
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          This is the only time you'll see the full key.
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            value={generatedPortalKey}
                            readOnly
                            className="bg-white dark:bg-gray-900 font-mono text-sm"
                          />
                          <Button
                            size="sm"
                            onClick={() => copyPortalKey(generatedPortalKey)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Key Button */}
              {!portalKeyStatus?.hasPortalKey && (
                <Button onClick={generatePortalKey} disabled={isGeneratingKey}>
                  {isGeneratingKey ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Key className="mr-2 h-4 w-4" />
                      Generate Public Portal Key
                    </>
                  )}
                </Button>
              )}
            </>
          )}

          {/* Integration Documentation */}
          {portalKeyStatus?.hasPortalKey && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() =>
                  setIsIntegrationDocsExpanded(!isIntegrationDocsExpanded)
                }
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    How to Use This Integration
                  </span>
                </div>
                {isIntegrationDocsExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </button>

              {isIntegrationDocsExpanded && (
                <div className="mt-4 space-y-4">
                  {/* Overview */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Overview
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      The public portal key enables seamless authentication from
                      external applications (e.g., SharePoint, internal
                      portals). When a user clicks a link in your external app,
                      they are automatically logged into the customer portal
                      without entering credentials.
                    </p>
                  </div>

                  {/* API Endpoint */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API Endpoint
                    </h5>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900 px-2 py-0.5 rounded">
                          GET
                        </span>
                        <code className="text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
                          /api/org/{"{orgSlug}"}/portal/public-auto-login
                        </code>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                        <p>
                          <strong>Query Parameters:</strong>
                        </p>
                        <ul className="list-disc list-inside ml-2 space-y-0.5">
                          <li>
                            <code className="font-mono">email</code> (required) -
                            Customer's email address
                          </li>
                          <li>
                            <code className="font-mono">key</code> (required) -
                            Your public portal key
                          </li>
                          <li>
                            <code className="font-mono">redirect</code>{" "}
                            (optional) - URL to redirect after login
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Code Example */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        JavaScript Example
                      </h5>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyCode}
                        className="h-7 px-2"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 rounded-md p-3 text-xs font-mono overflow-x-auto">
                      {`// Redirect user to customer portal
const baseUrl = '${import.meta.env.VITE_BACKEND_URL || apiConfig.baseUrl.replace("/api", "")}';
const orgSlug = '${organizationSubdomain || "your-org-slug"}';
const email = 'customer@example.com'; // Get from your user context
const portalKey = 'YOUR_PUBLIC_PORTAL_KEY';

const loginUrl = \`\${baseUrl}/api/org/\${orgSlug}/portal/public-auto-login?email=\${encodeURIComponent(email)}&key=\${portalKey}\`;

// Redirect user to auto-login
window.location.href = loginUrl;`}
                    </pre>
                  </div>

                  {/* Important Notes */}
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                        <p>
                          <strong>Important:</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>The customer must already exist in the system</li>
                          <li>
                            Keep your portal key secure - do not expose it in
                            client-side code
                          </li>
                          <li>
                            For server-side integrations, make a backend call to
                            construct the URL
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
